import React from "react";
import { RADIUS_OPTIONS, widerRadius, tradingHandle, shortCount} from "./data.jsx";
import { api, suggestionNameFor } from "./api.jsx";
import { track } from "./analytics.jsx";
import { useApp, useMedia } from "./store.jsx";
import { DrawBanner } from "./reveal.jsx";
import { CardAutocomplete } from "./autocomplete.jsx";
import { ModalHost, SectionHead, LoadMore, ListingGridSkeleton, GamePicker, CardLanguagePicker, LocationDialog, Linked, Dialog } from "./chrome.jsx";

const { SearchField, Select, Checkbox, Switch, ListingCard, Button, IconButton, Tabs, EmptyState, Card, Badge, Icon, Input } = window.CardboardmarketDesignSystem_2a3239;

/**
 * `games` and `onToggleGame` are passed in rather than read from the store,
 * because on Browse the game filter belongs to the URL — see BrowseScreen. The
 * trader's saved games seed it; the URL decides what is actually being shown.
 *
 * `languages` and its two handlers arrive the same way and for the same reason,
 * and so does `onAllGames`.
 */
function FilterControls({ games, onToggleGame, onAllGames, languages, onToggleLanguage, onAllLanguages, onlyWants, onOnlyWants, withPhotos, onWithPhotos, foil, onFoil, canWant }) {
  const { radius, setRadius, point, t } = useApp();
  const toggleGame = onToggleGame;
  const [locating, setLocating] = React.useState(false);
  return (
    <>
      {/*
        A distance filter needs somewhere to measure from.

        Without a point the server cannot narrow by distance at all — it returns
        everything — so a select reading "5 km" at the top of the filter rail is a
        claim about this list that is not true. The heading above the grid has said
        so all along; this is the rail agreeing with it instead of contradicting it.

        NOT `disabled`. The stored radius is a real preference and "5 km" is a true
        statement about it — the number is not wrong, it is merely sitting where it
        reads as a statement about the list. Greying it out would say the setting is
        unavailable, which is a second untrue thing rather than a repair, and it is
        the exact opposite of what LocationDialog decided: a radius is a useful
        thing to set on its own.

        So the slot holds the way OUT instead. The dialog it opens sets a point and
        a radius together, which is the whole of what is missing, and it is the same
        dialog the header opens rather than a second one that would drift.

        `!point`, not `!trader`. A signed-in trader who has never set a location is
        looking at exactly the same unfiltered list as a guest, and an authentication
        check would quietly leave them with the lie.
      */}
      {point
        ? <Select label={t("Distance")} options={RADIUS_OPTIONS} value={radius} onChange={setRadius} />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="cbm-label">{t("Distance")}</span>
            <Button variant="outline" size="sm" iconLeft="map-pin" onClick={() => setLocating(true)}>{t("Set where you are")}</Button>
            <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
              {t("Until then, distance filters nothing.")}
            </span>
          </div>
        )}
      <LocationDialog open={locating} onClose={() => setLocating(false)} />
      {/*
        "All games" FIRST, and it is what an empty selection looks like — the
        same control the languages row below gets, for the same reason. An unlit
        row of five chips reads as "nothing matches" to somebody who has never
        answered, when what it actually means is that everything is being shown.

        There is no stored `all`. It is the absence of a filter, rendered.
        Adding a sixth member to the `game` enum to say it would put "all" one
        careless insert away from being a card's actual game, and a card that
        belongs to every game is nonsense.

        Mutually exclusive with the specific chips, in both directions: picking
        All clears them, and picking one clears All. Two lit chips that
        contradict each other is a control nobody can read.

        The chips themselves are ChipPicker's — the same component the profile and
        onboarding use, so the three cannot drift apart again.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span className="cbm-label">{t("Game")}</span>
        <GamePicker selected={games} onToggle={toggleGame} onAll={onAllGames} />
      </div>
      {/*
        The language printed on the card, which is not the language you are
        reading this in. A Japanese print and an English print of the same card
        are different objects at different prices, and for a lot of traders it is
        the single fact that decides whether a listing is worth opening.

        Same control, same rules: "All languages" is the empty selection, and the
        three most likely answers show before the reveal. A language that arrived
        in the URL from somebody else's shared link is outside those three more
        often than not — Japanese aside — so the picker opens expanded whenever
        the selection holds one, because a filter nobody can see is a wrong
        result with no explanation on the screen.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span className="cbm-label">{t("Card language")}</span>
        {/* Narrowed to the games being browsed: a language filter offering
            Hungarian One Piece can only ever return nothing. */}
        <CardLanguagePicker selected={languages} onToggle={onToggleLanguage} onAll={onAllLanguages} games={games} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span className="cbm-label">{t("Filters")}</span>
        {/*
          The same filter the wants banner's "Show them" button applies, so the
          two controls cannot disagree about what is being shown.

          It used to be bound to `settings.alerts`, which defaulted to true and
          which nothing read — so it sat permanently ticked under a heading saying
          "Filters" and unticking it changed nothing. A control that looks like it
          filters and does not is worse than no control at all: the list it failed
          to change still gets believed.

          `alerts` is not gone; it moved to the only job it was ever describing,
          gating the wants banner above. It does not belong here, because hiding a
          notice and filtering a list are different requests.
        */}
        {/*
          NOT OFFERED TO SOMEBODY WITH NO WANTS LIST.

          Reported: it makes no sense for a guest. It made less than no sense —
          wants belong to an account, so the server answers a signed-out request
          for this with nothing at all, correctly and silently. The control was
          there, tickable, and guaranteed to empty the grid.

          Which is the exact fault the comment above this one is about: "a
          control that looks like it filters and does not is worse than no
          control at all". This one looked like it filtered and emptied the page
          instead.
        */}
        {canWant ? (
          <Checkbox label={t("Only cards on your wants list")} description={t("Matches against your wants list")} checked={onlyWants} onChange={onOnlyWants} />
        ) : null}
        {/*
          Photographs stopped being compulsory, so this is how you ask for them.

          Every listing shows a picture, and for most of them it is the
          publisher's catalogue artwork — it tells you what the card is printed
          like and nothing about the copy on offer. The description has to say
          which of the two this filters on, because "with photos" on a page where
          everything already has a picture reads as a control that does nothing.

          OFF by default. A listing without a photo is still a listing somebody
          can go and collect, and defaulting this on would hide a trader's whole
          imported stock from everyone who never opened the filters.
        */}
        <Checkbox label={t("Only cards the trader has photographed")} description={t("Hides listings that show only the catalogue picture")} checked={withPhotos} onChange={onWithPhotos} />
      </div>
      {/*
        THREE ANSWERS, NOT A SWITCH.
        
        "Hide the photographed ones" is nothing anybody wants, so photos is a
        checkbox. Foil is not like that: a collector hunts foils and somebody
        building a play set specifically does not want them — the premium is real
        money for a card that plays identically. A checkbox would serve one of
        those two and tell the other there is no such filter.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Select label={t("Foil")} value={foil || ''} onChange={onFoil}
          options={[
            { value: '', label: t("Foil or not") },
            { value: 'only', label: t("Only foils") },
            { value: 'none', label: t("No foils") },
          ]} />
      </div>
    </>
  );
}

/**
 * The nudge shown when a search finds nothing.
 *
 * It hands the search over to the wants screen rather than acting on it here.
 *
 * The first version added the want immediately and put the card's name ON the
 * button — "Watch for «Roronoa Zoro (OP01-001)»" — which does not fit a button and
 * never could, because a card name has no length limit. Worse, it acted before
 * anybody confirmed: one tap from a search box to a saved want, with the game
 * guessed from a filter.
 *
 * Carrying the search across and opening a prefilled form is the same number of
 * taps for the person and leaves the decision where it belongs. They see what
 * will be saved, and they can change the game or add a note before it is.
 */
function WantsPrompt({ query, game, picked }) {
  const { go, t } = useApp();
  const term = (query || '').trim();

  return (
    <Card tone="sunken" padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>{t("Can’t find it?")}</span>
      <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{t("Add it to your wants and we’ll alert you when it appears within your radius.")}</span>
      <Button size="sm" variant="outline" fullWidth onClick={() => go('you', {
        tab: 'wants',
        // What was searched, carried across so nobody types it twice.
        want: term || undefined,
        wantGame: term && game ? game : undefined,
        wantProduct: term && picked ? String(picked) : undefined,
      })}>{t("Add a want")}</Button>
    </Card>
  );
}

/**
 * The orderings, as the SERVER names them, with the sentences each one shows.
 *
 * Two vocabularies, deliberately kept apart. The key travels on the request and
 * is the thing the cursor pages on, so it must never be a translated sentence:
 * a request carrying "Más cerca" is a request the API has never heard of, and
 * switching language mid-session would silently stop the sort working. The label
 * is an English sentence because on this project the English sentence IS the
 * translation key — a missing translation then shows a real sentence rather than
 * a dotted path.
 *
 * `one` and `many` are the HEADING, and they live here rather than beside it so
 * that the ordering the request carries and the ordering the page claims cannot
 * be two different things. The heading read "closest first" under every sort,
 * which was already untrue when the sorting happened in the browser and became
 * plainly visible once the sort was real: somebody who asked for the cheapest
 * was told, above the grid, that they were looking at the nearest.
 *
 * Whole sentences rather than an ordering phrase dropped into a template. Two
 * entries per ordering for the same reason the singular has always been its own
 * entry — a translator should see the sentence they are translating, not a
 * fragment and a promise about where it lands.
 *
 * They are module constants, which is where KEYS belong on this project; only
 * what is rendered goes through t().
 */
const SORTS = [
  {
    key: 'closest', label: 'Closest',
    one: '1 listing within {radius}, closest first. Nobody posts anything — you hand it over.',
    many: '{count} listings within {radius}, closest first. Nobody posts anything — you hand it over.',
  },
  {
    key: 'price_asc', label: 'Price: low to high',
    one: '1 listing within {radius}, cheapest first. Nobody posts anything — you hand it over.',
    many: '{count} listings within {radius}, cheapest first. Nobody posts anything — you hand it over.',
  },
  {
    key: 'price_desc', label: 'Price: high to low',
    one: '1 listing within {radius}, most expensive first. Nobody posts anything — you hand it over.',
    many: '{count} listings within {radius}, most expensive first. Nobody posts anything — you hand it over.',
  },
  {
    key: 'newest', label: 'Newest',
    one: '1 listing within {radius}, newest first. Nobody posts anything — you hand it over.',
    many: '{count} listings within {radius}, newest first. Nobody posts anything — you hand it over.',
  },
];

export function BrowseScreen() {
  const { route, navigate, listings, listingsState, loadListings, go, radius, radiusKm, activeGames, activeLanguages, browseCounts, sort, setSort, settings, setRadius, wants, point, trader, cursors, loadMore, loadingMore, locale, unrevealed, t } = useApp();
  /**
   * Filters live in the URL, not in component state.
   *
   * A search someone found useful is exactly the thing they want to send to a
   * friend, and a view whose address never changes cannot be shared honestly. It
   * also means back and forward move through searches, which is what people
   * already expect from a browser.
   *
   * `replace` rather than push, so typing five characters leaves ONE history entry
   * instead of five — otherwise the back button becomes a backspace key.
   */
  const q = route.q || '';
  const tab = route.mode || 'all';
  /*
   * `activeGames` and `activeLanguages` are READ FROM THE STORE, not worked out
   * again here.
   *
   * The rule has not changed — the URL wins whenever it says anything, and the
   * trader's saved lists only seed the first view, because the recipient of a
   * shared link is not its sender. What changed is where it is applied: the
   * store resolves them and builds the request from them, so the grid, the tab
   * counts and these controls cannot disagree about what is being shown. They
   * used to be resolved here and applied to the page that had arrived, which is
   * exactly the arrangement where a count describes a different list than the
   * grid beneath it.
   */
  /*
   * "All games" is a VALUE the URL can hold, not the absence of one.
   *
   * It was the absence of one, and that is why the button did nothing. An absent
   * `game` means "no opinion", and no opinion falls back to the games saved on
   * the account — so clearing the filter brought the chips straight back, and
   * unlighting the last chip did the same thing by a second door. The URL had two
   * states where the screen has three.
   *
   * `?game=` is the third: present, and empty. A link carrying it means "all
   * games" to whoever opens it, which is the URL-wins rule doing its job rather
   * than being worked around. See `toPath`/`fromLocation` in router.jsx, which
   * had to learn to keep an empty repeatable key instead of dropping it.
   *
   * Applied to the carried-forward defaults too, or there would be a third door:
   * changing the sort while showing all games would re-read the saved list.
   */
  const selection = (list) => (list.length ? list : '');
  const setFilters = (patch) => navigate('browse', {
    q, mode: tab === 'all' ? undefined : tab, game: selection(activeGames),
    language: selection(activeLanguages),
    // Carried unless the patch says otherwise, so changing a game does not silently
    // drop the wants view somebody is looking at — or the photo filter.
    only: route.only,
    photos: route.photos,
    // And the foil filter and the order, which were dropped by every other
    // change until 2026-10-22: the link is what a reload restores.
    foil: route.foil,
    // The store's order, not the address's: a browse link written elsewhere
    // without one (the wants screen's search) must not leave it out from then on.
    sort: sort !== 'closest' ? sort : undefined,
    // The picked card's English name, which the search reads (see `pickCard` below).
    card: route.card,
    ...patch,
  }, { replace: true });
  /* Into the store, which asks the server, and into the link, which a reload reads. See `linkedSort`. */
  const chooseSort = (key) => { setSort(key); setFilters({ sort: key === 'closest' ? undefined : key }); };
  /*
   * Picking a game deselects "All", because the list is what "All" is: as soon
   * as one chip is lit the empty selection is gone, and unlighting the last one
   * brings it back. There is no third state to keep in step.
   */
  const toggleGame = (g) => setFilters({
    game: selection((activeGames.includes(g) ? activeGames.filter((x) => x !== g) : [...activeGames, g]).filter(Boolean)),
  });
  const allGames = () => setFilters({ game: '' });
  /*
   * Picking a language deselects "All", because the list is what "All" is: as
   * soon as one chip is lit the empty selection is gone, and unlighting the last
   * one brings it back. There is no third state to keep in step.
   */
  const toggleLanguage = (code) => setFilters({
    language: selection((activeLanguages.includes(code) ? activeLanguages.filter((x) => x !== code) : [...activeLanguages, code]).filter(Boolean)),
  });
  const allLanguages = () => setFilters({ language: '' });
  const setQ = (value) => setFilters({ q: value || undefined, card: undefined });
  /*
   * A PICKED SUGGESTION: the box keeps the name it was found by ("Elfos de
   * Llanowar"), and the link carries the card's English name beside it as
   * `card`, which is what the request searches — browse's own search reads
   * English titles only, and matching other languages in there is the
   * regression of 2026-10-22 (see `cardFilters` in the API). Both travel in
   * the link, so a reload or a shared link shows the same box and asks once.
   * Typing again drops `card`: the text is the search again.
   */
  const pickCard = (item) => setFilters({
    q: suggestionNameFor(item),
    card: suggestionNameFor(item) !== item.name ? item.name : undefined,
    game: item.game ? [item.game] : activeGames,
  });

  const setTab = (value) => setFilters({ mode: value === 'all' ? undefined : value });
  /*
   * The sort lives in the STORE now, beside the filters, because it is part of
   * the request rather than a way of arranging what arrived. Changing it re-asks
   * from the first page exactly as changing a filter does — a cursor is a
   * position in one ordering and means nothing in another.
   */
  // Which catalogue product the searcher chose, if they chose one rather than
  // typing. Carried into a want so it follows the card. Cleared on every keystroke,
  // because after editing the text the old pick is no longer what they mean.
  const [pickedProduct, setPickedProduct] = React.useState(null);
  // Labels for the Select, and the way back from a chosen label to its key. The
  // list is built once so the mapping is a lookup rather than a comparison against
  // a translation — which is the shape that goes wrong when the language changes.
  /*
   * "CLOSEST" NEEDS SOMEWHERE TO BE CLOSE TO.
   *
   * Reported alongside the wants filter, and worse than it, because closest is
   * the DEFAULT: a visitor who has not said where they are was being shown a
   * list ordered by distance from nothing. The screen even says so two controls
   * above — "until then, distance filters nothing" — while the sort underneath
   * claimed to be doing it.
   *
   * The server already behaves correctly: with no origin every distance is
   * null, NULLS LAST puts them all in the tie-break, and the result is the
   * newest-first ordering. So the grid was right and only the label was lying.
   * Offering it is what had to stop.
   *
   * ON THE POINT, NOT ON THE ACCOUNT, and that distinction matters: a guest who
   * drops a pin can sort by distance perfectly well, and a signed-in trader who
   * has never set one cannot. Wants need an account; closest needs a location.
   */
  const canSortByDistance = Boolean(point);
  const offered = SORTS.filter((s) => s.key !== 'closest' || canSortByDistance);
  const sortLabels = offered.map((s) => t(s.label));
  const sortKey = (label) => (offered[sortLabels.indexOf(label)] ?? offered[0]).key;
  // The ordering in force, resolved ONCE. The Select's value and the sentence in
  // the heading are two readings of the same row, so they cannot disagree about
  // what the grid beneath them is doing.
  /*
   * What the grid is ACTUALLY ordered by. A URL can still carry `sort=closest`
   * — shared, bookmarked, or arrived at before the pin was cleared — and the
   * answer then is the ordering the server really applies, not the one the
   * link asked for.
   */
  const ordering = offered.find((s) => s.key === sort)
    /*
     * WHAT THE SERVER ACTUALLY DOES, not the first thing left in the list.
     *
     * `offered[0]` was price-ascending once closest was filtered out, so a URL
     * carrying `sort=closest` without a pin would have been labelled "cheapest
     * first" over a grid the server had ordered newest-first. That is the same
     * lie this change exists to remove, moved one line down — which is what
     * makes a fallback worth thinking about rather than reaching for the first
     * element.
     *
     * With no origin every distance is null and NULLS LAST drops them all into
     * the tie-break, which IS the newest ordering. So that is the honest label.
     */
    ?? SORTS.find((s) => s.key === 'newest')
    ?? offered[0];
  const sortLabel = ordering.label;
  const [sheet, setSheet] = React.useState(false);
  const compact = useMedia('(max-width:760px)');

  /*
   * THE SERVER DECIDES WHAT IS ON THIS SCREEN. All of it.
   *
   * This used to be a filter and a sort over `listings` — the page that had
   * arrived — and both were wrong in the same way. Filtering to Japanese showed
   * the Japanese cards among the fifty loaded, so a rare language read as
   * "almost nothing here" when the truth was "almost nothing in the first
   * fifty"; and "Price: low to high" topped the grid with the cheapest of those
   * fifty rather than the cheapest within the radius. Filtering fetched
   * information is an incomplete kind of filtering.
   *
   * The radius was always the exception, and the reason it was is now the reason
   * everything else joined it: the server is the only side holding precise
   * distances, because what browse returns is coarsened and floored at a
   * kilometre. Re-filtering here on that number would drop listings that
   * legitimately sit just inside the edge. THE RADIUS MUST NEVER MOVE HERE.
   *
   * Changing any filter, or the sort, re-reads from the first page — see
   * `browseQuery` in store.jsx. A cursor is a position within the population a
   * query defines, so resuming one under a different filter pages a set it was
   * never cut from.
   */
  const list = listings;
  const FIRST_PAINT = 24;
  const [painted, setPainted] = React.useState(FIRST_PAINT);
  React.useEffect(() => { setPainted(FIRST_PAINT); }, [listings]);
  React.useEffect(() => {
    if (painted >= list.length) return undefined;
    const id = requestAnimationFrame(() => setPainted(list.length));
    return () => cancelAnimationFrame(id);
  }, [painted, list.length]);
  /*
   * The tab counts, from the server, each with every OTHER filter applied.
   *
   * That is what makes them facet counts, and why "For sale 12 · For trade 5"
   * can both be right at once. Null before the first page lands, and the tabs
   * then show nothing rather than a zero they cannot stand behind.
   */
  const count = (mode) => (browseCounts ? browseCounts[mode] : undefined);
  /*
   * "500+" rather than "500", where the server stopped counting.
   *
   * The count used to walk every matching listing, which cost 3.2 seconds on a
   * market of 350,000 — the slowest thing on the screen by far, to produce a
   * number nobody reads precisely. It stops at a cap now, so the work is
   * proportional to the cap rather than to the market.
   *
   * WHETHER IT WAS CAPPED COMES FROM THE SERVER, per count, never from
   * comparing against a number written here: only the server knows where the
   * cap is, and a client that guessed would start lying the day it moved. Per
   * count because a market can hold nine hundred cards for sale and eleven for
   * trade, and one flag cannot describe both.
   */
  const countLabel = (mode) => {
    const n = count(mode);
    if (n === undefined) return undefined;
    const more = browseCounts && browseCounts.more && browseCounts.more[mode];
    /*
     * Shortened past a thousand — "1,2 mil", "2 M" — because at that size the
     * digits stop being information. See `shortCount`, which follows the
     * locale's own convention rather than imposing English K and M.
     *
     * The exact counts are capped at 500 so they never reach it; in practice
     * only the ballpark below is ever shortened. It is applied to both anyway,
     * so raising the cap cannot quietly put a six-digit number back on screen.
     */
    if (!more) return shortCount(n, locale);
    /*
     * The market's real size where the server could estimate it, and "500+"
     * where it could not. The server sends no ballpark at all rather than one
     * it does not trust, so the presence of the number IS the decision — this
     * side does not second-guess it.
     *
     * NO ROUNDING HERE ANY MORE: `shortCount` does it, and better. Two
     * significant figures by hand produced "2,000,000", which reads as a
     * precise count of exactly two million; "2 M" reads as the estimate it is.
     */
    const guess = browseCounts.about && browseCounts.about[mode];
    return guess ? t("about {n}", { n: shortCount(guess, locale) }) : `${shortCount(n, locale)}+`;
  };
  /*
   * How many the current tab holds, for the sentences that quote a number.
   *
   * The count rather than `list.length`, because `list` is one page of sixty and
   * the sentence is about the radius. It falls back to the page only while the
   * counts have not arrived, where the two agree anyway.
   */
  const total = count(tab) ?? list.length;
  /*
   * THE SAME NUMBER, AS A SENTENCE WOULD SAY IT.
   *
   * `total` stays numeric for the analytics below and for the singular test;
   * this is what the prose quotes. Capped, "500 listings within 15 km" would be
   * a flat lie about a market holding two and a half million — the count stops
   * at the cap, so the sentence has to stop with it.
   *
   * It reuses `countLabel` rather than repeating the rule, because the tab and
   * the sentence above it disagreeing about the same number is exactly the kind
   * of thing nobody notices until a user does.
   */
  const totalLabel = countLabel(tab) ?? total;
  /*
   * "Only cards I am watching for" — a view, not a search term.
   *
   * Lives in the URL like every other filter, so it can be shared and the back
   * button undoes it, and it is applied by the server as a join against your own
   * want rows. It used to be a lowercased title comparison here against the
   * wants this screen happened to be holding, over the listings that happened to
   * have arrived — which is the thing migration 025 was written to stop.
   */
  const onlyWants = route.only === 'wants';
  /** "Only cards the trader has photographed" — in the URL, applied by the server. */
  const withPhotos = route.photos === '1';
  // 'only' | 'none' | undefined — see FilterControls for why it is not a boolean.
  const foil = route.foil === 'only' || route.foil === 'none' ? route.foil : undefined;

  /*
   * The next rung up, or nothing at all.
   *
   * This button used to say "Widen to 15 km" whatever radius you were on, and it
   * was wrong on two of the four rungs: at 15 km it offered the radius you had
   * and did nothing, and at 50 km it said "widen" while NARROWING you to 15 and
   * throwing away results. A button that names a value has to read that value.
   *
   * `undefined` at the widest rung, and the empty state then renders no widen
   * button at all rather than a disabled one. A disabled control still reads as
   * the way forward — it says "this is what you should do, but not now" — when
   * the truth is that this is not the thing to do at all. See `widerRadius` in
   * data.jsx, which owns the ladder the Selects also read.
   */
  const wider = widerRadius(radius);
  /*
   * Whether "drop a filter" is advice somebody can act on.
   *
   * Offered only when something is actually narrowing the list, or the fallback
   * would repeat the bug it replaces: a Clear filters button with no filters to
   * clear does nothing, exactly as "Widen to 15 km" at 15 km did nothing.
   *
   * The TAB is deliberately not counted. It narrows the list too, but the tab bar
   * above carries its own counts, so somebody looking at an empty "For trade" can
   * already see where the listings are — and moving their tab out from under them
   * is not what "clear filters" should mean.
   */
  const hasFilters = Boolean(q) || activeGames.length > 0 || activeLanguages.length > 0 || onlyWants || withPhotos || Boolean(foil);
  const clearFilters = () => setFilters({ q: undefined, card: undefined, game: '', language: '', only: undefined, photos: undefined });

  React.useEffect(() => {
    if (!q) return;
    // Debounced by the URL settling. The value is the result count: a search that
    // returns nothing is the most useful signal this product has, because it is
    // somebody looking for a card the marketplace cannot offer them.
    const timer = setTimeout(() => track('search', { value: total }), 800);
    return () => clearTimeout(timer);
  }, [q, total]);

  /*
   * Wants that have turned up inside the radius — the banner below.
   *
   * `settings.alerts` gates it, which is the whole of what that setting does.
   * Before this it did nothing at all, and the switch offering to turn it off
   * had never been able to.
   */
  /*
   * From the WANTS endpoint, which counted them, not from the browse page.
   *
   * It compared lowercased titles against whichever listings had arrived, so it
   * could only ever notice a want among the first sixty — and only when the two
   * spellings matched exactly, which migration 025 exists because they usually
   * do not. It would also have started narrowing with the filters the moment
   * they moved to the server: filtering to Japanese would have hidden the banner
   * about a want that is sitting two kilometres away in English.
   *
   * `matchesWithinRadius` is the same metacard-or-title join, counted server-side
   * against the account's radius. See routes/wants.ts.
   */
  const matched = settings.alerts ? wants.filter((w) => (w.matchesWithinRadius ?? 0) > 0) : [];
  return (
    <>
      {/*
        Without a point, "within 5 km" is a claim we did not make: the server
        cannot filter by a distance it does not have, so it returns everything and
        the radius in the header would be decoration on an unfiltered list. Say
        what is true instead, and say what to do about it.
      */}
      <SectionHead title={t("Cards near you")} sub={!point
        ? t("Set where you are and this becomes a list of cards you could go and collect. Until then it is everything on the site.")
        /*
          While the skeletons are up, the count is not a count — it is zero
          because nothing has arrived yet, and "0 listings within 15 km" is a
          claim about the world made before we asked. Same condition as the
          skeleton grid on purpose: the heading and the thing beneath it should
          never describe different situations.

          A filter change keeps the previous grid, so it keeps the previous
          count and this does not flicker on every chip.
        */
        : listingsState === 'loading' && !list.length
        ? t("Finding listings within {radius}.", { radius })
        // Which ordering, from the ordering itself — see SORTS. This said
        // "closest first" whatever was selected, so asking for the cheapest got
        // you a heading describing a sort you had just changed away from.
        //
        // Two entries rather than a pluralisation rule: Spanish and English happen
        // to agree here, but the singular is a whole sentence either way and a
        // translator should see it as one.
        : total === 1
        ? t(ordering.one, { radius })
        : t(ordering.many, { count: totalLabel, radius })}
        right={<div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', width: compact ? '100%' : 380 }}>
          <div style={{ flex: 1 }}>
            {/*
              SearchField takes a bare value rather than an event, so it is passed
              through as the field and the change is normalised inside.
            */}
            <CardAutocomplete
              Field={SearchField}
              label={undefined}
              placeholder={t("Card, set, or @trader")}
              value={q}
              game={activeGames.length === 1 ? activeGames[0] : undefined}
              onChange={(v) => { setQ(v); setPickedProduct(null); }}
              onPick={(item) => {
                setPickedProduct(item.productId || null);
                pickCard(item);
              }}
            />
          </div>
          <span className="cbm-narrow"><IconButton icon="sliders-horizontal" label={t("Filters")} onClick={() => setSheet(true)} /></span>
        </div>} />

      {onlyWants ? (
        <Card cut padding="var(--space-5)" style={{ marginBottom: 'var(--space-6)', display: 'flex', gap: 'var(--space-5)', alignItems: 'center', flexWrap: 'wrap' }}>
          <Icon name="bookmark" size={20} />
          <div style={{ flex: 1, minWidth: 220, font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>
            {t("Only cards on your wants list")}
          </div>
          {/* A filter you cannot see is a filter you will blame the market for. */}
          <Button variant="secondary" size="sm" onClick={() => setFilters({ only: undefined })}>{t("Show everything")}</Button>
        </Card>
      ) : matched.length ? (
        <Card cut padding="var(--space-5)" style={{ marginBottom: 'var(--space-6)', display: 'flex', gap: 'var(--space-5)', alignItems: 'center', flexWrap: 'wrap', boxShadow: 'var(--shadow-hard)' }}>
          <Icon name="bookmark" size={20} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>
              {matched.length === 1
                ? t("1 of your wants is within {radius}", { radius })
                : t("{count} of your wants are within {radius}", { count: matched.length, radius })}
            </div>
            <div style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>{matched.map((m) => m.title).join(' · ')}</div>
          </div>
          {/*
            Show ALL of them, not the first one.
            The button said "Show them" and then filtered to a single want — the
            one that happened to sort first — so a banner announcing three matches
            showed you one card and quietly dropped the other two. There is no
            search term that means "these three cards", so this stops pretending
            there is and switches the list to a wants view instead.

            The filters go too: a want match that a game filter is hiding is
            exactly the case where the filter is the problem.
          */}
          <Button variant="secondary" size="sm" onClick={() => setFilters({ q: undefined, card: undefined, game: undefined, language: undefined, mode: undefined, only: 'wants' })}>
            {t("Show them")}
          </Button>
        </Card>
      ) : null}

      {/*
        A result nobody has looked at, at the top of browse.

        One of the three places the design names for it — the others are the
        invite screen and the draw's own page. A full banner rather than the
        shell's strip because there is room here and the moment is worth it; the
        strip stands down on this screen while it is showing, so the product
        says it once.
      */}
      {unrevealed ? <div style={{ marginBottom: 'var(--space-7)' }}><DrawBanner campaign={unrevealed} /></div> : null}

      <div className="cbm-rail">
        <aside className="cbm-rail__aside cbm-wide">
          <FilterControls games={activeGames} onToggleGame={toggleGame} onAllGames={allGames}
            languages={activeLanguages} onToggleLanguage={toggleLanguage} onAllLanguages={allLanguages}
            canWant={Boolean(trader)}
            onlyWants={onlyWants} onOnlyWants={(on) => setFilters({ only: on ? 'wants' : undefined })}
            withPhotos={withPhotos} onWithPhotos={(on) => setFilters({ photos: on ? '1' : undefined })}
            foil={foil} onFoil={(v) => setFilters({ foil: v || undefined })} />
          <Select label={t("Sort by")} options={sortLabels} value={t(sortLabel)} onChange={(label) => chooseSort(sortKey(label))} />
          <WantsPrompt query={route.card || q} game={activeGames.length === 1 ? activeGames[0] : null} picked={pickedProduct} />
        </aside>
        <section>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-5)', marginBottom: 'var(--space-6)', flexWrap: 'wrap' }}>
            <Tabs label={t("Listings")} value={tab} onChange={setTab} items={[
              { value: 'all', label: t("Everything"), count: countLabel('all') },
              { value: 'sale', label: t("For sale"), count: countLabel('sale') },
              { value: 'trade', label: t("For trade"), count: countLabel('trade') },
            ]} />
            <span className="cbm-mob"><Button size="sm" variant="secondary" iconLeft="plus" onClick={() => go('post')}>{t("Post a card")}</Button></span>
          </div>
          {list.length ? (
            <>
            <div className="cbm-grid-listings">
              {/*
                Who is offering it, under the card.
                Not passed INTO ListingCard: it has no slot for a seller and would
                forward the prop onto its <article> as an HTML attribute. Beneath is
                also the honest place for it — the card is the thing, the person is
                who you would be meeting.
              */}
              {list.slice(0, painted).map((l) => (
                <div key={l.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {/* A real link, so middle-click and ctrl-click work. See Linked. */}
                  <Linked to={{ name: 'listing', id: l.id }}>
                    <ListingCard {...api.listingCardProps(l, t)} />
                  </Linked>
                  {/*
                    GATED ON WHAT IS ACTUALLY DRAWN, not on the trader's handle.

                    Reported with a screenshot of browse: "shops' listings do not
                    show their handle or anything below the listing". A shop's
                    TRADER handle is null — the shop carries its own, and
                    `tradingHandle` three lines down has always known that — so
                    this gate threw away the whole line, store name and trade
                    count included, for every shop in the feed. The expression
                    below was already right; the condition above it was checking
                    a different thing and happened to agree for people.
                  */}
                  {tradingHandle(l.seller) || l.sellerHandle ? (
                    <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', paddingLeft: 2, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {/*
                        A VERIFIED STORE BY ITS OWN NAME, everybody else by
                        handle.

                        The line only ever said `@asier-p`, which for a store is
                        the owner's handle — the one thing a shop is not trading
                        under. The name is what a buyer recognises, so it leads
                        and the handle follows it as the identifier it is.

                        Only the NAME arrives on a browse row: the address and
                        the point stay on the full record of one listing, because
                        a shop's coordinate on every row of a feed is payload
                        nobody asked for. See the note in routes/listings.ts.
                      */}
                      {l.seller && l.seller.shop && l.seller.shop.name ? (
                        <>
                          <span style={{ color: 'var(--text-strong)' }}>{l.seller.shop.name}</span>
                          <span aria-hidden="true">·</span>
                        </>
                      ) : null}
                      <span>@{tradingHandle(l.seller) || l.sellerHandle}</span>
                      {/* Completed trades, including none. "New here" is a real
                          answer to "should I meet this person", and it is the answer
                          for almost everybody in a marketplace this young. */}
                      <span aria-hidden="true">·</span>
                      <span>{l.sellerTrades ? t("{count} trades", { count: l.sellerTrades }) : t("new here")}</span>
                      {l.sellerFlagged ? (
                        <span style={{ color: 'var(--orange-600)' }}>· {t("Reported")}</span>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
            {/*
              Under the grid, and the cursor now belongs to THIS list.
              It used to belong to the unfiltered feed, because the filtering
              happened here: a "show more" beneath a filtered view fetched a page
              that mostly did not match and appeared to do nothing. The server
              pages the filtered, sorted population now, so the next page is the
              next sixty of what is actually on screen.
            */}
            <LoadMore cursor={cursors.listings} busy={loadingMore === 'listings'} onClick={() => loadMore('listings')} />
            </>
          ) : listingsState === 'error' ? (
            /*
             * A failure is not an empty market.
             *
             * This branch did not exist: a 500 rendered "nothing within your
             * radius", which tells somebody there are no cards near them — a
             * claim about the world, when the truth is we could not ask. It is
             * the more discouraging of the two answers and it was the wrong one.
             *
             * `listingsState` has been computed and handed to every screen since
             * the app was wired up, and no screen has ever read it.
             */
            <EmptyState icon="alert-triangle" title={t("We could not load the listings")}
              description={t("Something went wrong at our end, not yours. Your radius and filters are untouched.")}
              action={<Button onClick={() => loadListings()}>{t("Try again")}</Button>} />
          ) : listingsState === 'loading' ? (
            /*
             * The grid's OWN shape, in the grid's own container.
             *
             * This was an EmptyState — one small box standing where a grid of
             * cards was about to appear — so a first visit went chrome-and-a-box
             * and then chrome-and-a-full-grid. Nothing about it was slow. The
             * jump was, and a jump is what people report as slowness.
             *
             * Only reachable when the list is genuinely empty, which is the first
             * navigation here. `loadListings` deliberately keeps the previous
             * grid on screen while a FILTER change loads, and that stays: the
             * cards already on screen are the best answer available until better
             * ones arrive.
             *
             * The same `cbm-grid-listings` as the real grid, so the columns and
             * the gaps cannot be a second opinion about the layout.
             */
            <ListingGridSkeleton label={t("Finding what is within your radius.")} />
          ) : (
            <EmptyState icon="layers" title={t("Nothing within your radius yet")}
              /*
               * The advice has to be advice you can take.
               *
               * "Widen the radius or drop a filter" is true on three rungs and
               * false on the fourth, where there is no wider radius to move to —
               * and the sentence sits directly above a button that used to offer
               * one anyway.
               */
              description={wider
                ? t("No listings match these filters. Widen the radius or drop a filter — every extra kilometre usually adds a few cards.")
                : hasFilters
                ? t("No listings match these filters. {radius} is as wide as the radius goes, so try dropping one.", { radius })
                : t("Nothing is listed within {radius} of you yet, and that is as wide as the radius goes.", { radius })}
              action={wider
                ? <Button onClick={() => setRadius(wider)}>{t("Widen to {radius}", { radius: wider })}</Button>
                : hasFilters
                ? <Button onClick={clearFilters}>{t("Clear filters")}</Button>
                // Nothing to widen and nothing to drop. No button: an empty
                // market is not a thing this screen can do anything about, and
                // inventing an action for it would be inventing a way out.
                : undefined} />
          )}
        </section>
      </div>

      <ModalHost>
        {sheet ? (
          <Dialog open closeLabel={t("Close")} title={t("Filters")} onClose={() => setSheet(false)} width={420} footer={<Button fullWidth onClick={() => setSheet(false)}>{total === 1 ? t("Show 1 listing") : t("Show {count} listings", { count: totalLabel })}</Button>}>
            {/* Same ratio as the desktop rail: the sheet has the same four groups
                and the same problem if they sit too close to tell apart. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
              <FilterControls games={activeGames} onToggleGame={toggleGame} onAllGames={allGames}
            languages={activeLanguages} onToggleLanguage={toggleLanguage} onAllLanguages={allLanguages}
            canWant={Boolean(trader)}
            onlyWants={onlyWants} onOnlyWants={(on) => setFilters({ only: on ? 'wants' : undefined })}
            withPhotos={withPhotos} onWithPhotos={(on) => setFilters({ photos: on ? '1' : undefined })}
            foil={foil} onFoil={(v) => setFilters({ foil: v || undefined })} />
              <Select label={t("Sort by")} options={sortLabels} value={t(sortLabel)} onChange={(label) => chooseSort(sortKey(label))} />
            </div>
          </Dialog>
        ) : null}
      </ModalHost>
    </>
  );
}

